import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Panel, PanelHeader, PanelHeaderBack, Div, Spinner, Text,
  Card, Headline, Progress, Button,
} from '@vkontakte/vkui';
import {
  Icon28WalletOutline, Icon28CheckCircleOutline, Icon28UsersOutline, Icon28ServicesOutline,
} from '@vkontakte/icons';
import { api } from '../../api/client';
import { useWeddingStore } from '../../stores/wedding';

type Tab = 'budget' | 'tasks' | 'guests' | 'vendors';

interface BudgetA {
  budget: number | null;
  currency: string;
  planned: number;
  paid: number;
  unpaid: number;
  remaining: number | null;
  over_budget: boolean;
  by_category: { name: string; amount: number; count: number }[];
  unpaid_payments: { name: string; amount: number; due_date: string | null; expense: string }[];
}
interface TasksA {
  total: number; done: number; open: number; overdue: number; upcoming: number;
  progress_pct: number;
  by_category: { name: string; total: number; done: number }[];
  subtasks_total: number; subtasks_done: number;
}
interface GuestsA {
  total: number;
  by_age: { adult: number; child: number; baby: number };
  rsvp: { accepted: number; pending: number; denied: number };
  not_invited: number;
  by_group: { name: string; count: number }[];
  couples: number;
}
interface VendorsA {
  total: number;
  by_status: { pending: number; reserved: number; rejected: number };
  amount_total: number;
  by_category: { name: string; count: number; amount: number }[];
}

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'budget', label: 'Бюджет', icon: <Icon28WalletOutline /> },
  { id: 'tasks', label: 'Чек-лист', icon: <Icon28CheckCircleOutline /> },
  { id: 'guests', label: 'Гости', icon: <Icon28UsersOutline /> },
  { id: 'vendors', label: 'Подрядчики', icon: <Icon28ServicesOutline /> },
];

function fmt(n: number, cur = 'RUB') {
  const s = new Intl.NumberFormat('ru-RU').format(Math.round(n));
  return `${s} ${cur === 'RUB' ? '₽' : cur}`;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Headline level="2" style={{ margin: '4px 0 12px', fontSize: 16 }}>
      {children}
    </Headline>
  );
}

function StatCard({ title, value, sub, accent }: {
  title: string; value: string | number; sub?: string; accent?: boolean;
}) {
  return (
    <Card style={{
      padding: 14, flex: 1, minWidth: 96,
      background: accent ? 'var(--vkui--color_background_accent)' : 'var(--vkui--color_background_secondary)',
      color: accent ? '#fff' : undefined,
    }}>
      <Text style={{
        fontSize: 12, lineHeight: 1.3,
        color: accent ? 'rgba(255,255,255,0.8)' : 'var(--vkui--color_text_secondary)',
      }}>{title}</Text>
      <Headline level="1" style={{ marginTop: 6, fontSize: 22, color: accent ? '#fff' : undefined }}>
        {value}
      </Headline>
      {sub && (
        <Text style={{
          fontSize: 12, marginTop: 2,
          color: accent ? 'rgba(255,255,255,0.8)' : 'var(--vkui--color_text_secondary)',
        }}>{sub}</Text>
      )}
    </Card>
  );
}

function BarRow({ label, value, max, color, suffix }: {
  label: string; value: number; max: number; color?: string; suffix?: string;
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: 8 }}>
        <Text style={{ fontSize: 14, flex: 1 }}>{label}</Text>
        <Text style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {value}{suffix ? ` ${suffix}` : ''} · {pct}%
        </Text>
      </div>
      <div style={{
        height: 10, borderRadius: 5, overflow: 'hidden',
        background: 'var(--vkui--color_background_secondary)',
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: 5,
          background: color || 'var(--vkui--color_background_accent)',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { currentWedding } = useWeddingStore();
  const [tab, setTab] = useState<Tab>('budget');
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState<BudgetA | null>(null);
  const [tasks, setTasks] = useState<TasksA | null>(null);
  const [guests, setGuests] = useState<GuestsA | null>(null);
  const [vendors, setVendors] = useState<VendorsA | null>(null);
  const [restricted, setRestricted] = useState<string[]>([]);

  const load = useCallback(async () => {
    if (!currentWedding) return;
    setLoading(true);
    try {
      const data = await api.get<{
        budget?: BudgetA; tasks?: TasksA; guests?: GuestsA; vendors?: VendorsA;
        restricted?: string[];
      }>(`weddings/${currentWedding.id}/analytics/overview`);
      setBudget(data.budget ?? null);
      setTasks(data.tasks ?? null);
      setGuests(data.guests ?? null);
      setVendors(data.vendors ?? null);
      setRestricted(data.restricted ?? []);
    } catch {
      /* keep old */
    } finally {
      setLoading(false);
    }
  }, [currentWedding]);

  useEffect(() => {
    void load();
  }, [load]);

  // Per-block permissions: restricted blocks are excluded from /overview
  const tabs = TABS.filter((t) => !restricted.includes(t.id));
  const activeTab: Tab | null = tabs.some((t) => t.id === tab) ? tab : (tabs[0]?.id ?? null);

  if (!currentWedding) {
    return (
      <Panel>
        <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>Аналитика</PanelHeader>
        <Div>Сначала создайте или выберите свадьбу</Div>
      </Panel>
    );
  }

  const cur = budget?.currency || 'RUB';

  return (
    <Panel>
      <PanelHeader before={<PanelHeaderBack onClick={() => navigate('/')} />}>
        Аналитика
      </PanelHeader>

      <Div style={{ paddingTop: 12, paddingBottom: 4 }}>
        <Headline level="1" style={{ fontSize: 22 }}>Сводка по свадьбе</Headline>
        <Text style={{ marginTop: 4, color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
          {currentWedding.name}
        </Text>
      </Div>

      {/* Tab selector — 2×2 grid, no truncation */}
      <Div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, paddingTop: 8 }}>
        {tabs.map((t) => {
          const active = activeTab === t.id;
          return (
            <Card
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '12px 10px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                cursor: 'pointer',
                background: active ? 'var(--vkui--color_background_accent)' : 'var(--vkui--color_background_secondary)',
                color: active ? '#fff' : 'var(--vkui--color_text_primary)',
                border: active ? '2px solid var(--vkui--color_background_accent)' : '2px solid transparent',
              }}
            >
              <span style={{ color: active ? '#fff' : 'var(--vkui--color_text_secondary)' }}>{t.icon}</span>
              <Text style={{
                fontSize: 13, fontWeight: active ? 600 : 400,
                color: active ? '#fff' : 'var(--vkui--color_text_primary)',
                textAlign: 'center',
              }}>{t.label}</Text>
            </Card>
          );
        })}
      </Div>

      {loading && (
        <Div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner size="m" />
        </Div>
      )}

      {!loading && !activeTab && (
        <Div style={{ paddingTop: 24, textAlign: 'center' }}>
          <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Нет доступных разделов</Text>
        </Div>
      )}

      {/* ===== BUDGET ===== */}
      {!loading && activeTab === 'budget' && budget && (
        <Div style={{ paddingTop: 16 }}>
          {budget.over_budget && (
            <Card style={{
              padding: 14, marginBottom: 16,
              background: 'var(--vkui--color_background_negative)',
            }}>
              <Text style={{ fontWeight: 600 }}>Превышен бюджет</Text>
              <Text style={{ marginTop: 4, fontSize: 13 }}>
                Планируемые расходы превышают бюджет на {fmt(budget.planned - (budget.budget || 0), cur)}
              </Text>
            </Card>
          )}

          <SectionTitle>Общий бюджет</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
              Запланировано
            </Text>
            <Headline level="1" style={{ fontSize: 28, marginTop: 2 }}>
              {fmt(budget.planned, cur)}
            </Headline>
            <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)', marginTop: 4 }}>
              из {budget.budget != null ? fmt(budget.budget, cur) : 'бюджет не задан'}
            </Text>
            <Progress
              value={budget.budget ? Math.min(100, (budget.planned / budget.budget) * 100) : 0}
              style={{ marginTop: 12, height: 8 }}
            />
          </Card>

          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <StatCard title="Уже оплачено" value={fmt(budget.paid, cur)} />
            <StatCard title="Ещё оплатить" value={fmt(budget.unpaid, cur)} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <StatCard
              title="Остаток бюджета"
              value={budget.remaining != null ? fmt(budget.remaining, cur) : '—'}
              accent={budget.remaining != null && budget.remaining < 0}
            />
          </div>

          <SectionTitle>Расходы по категориям</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            {budget.by_category.length === 0 && (
              <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Расходов пока нет</Text>
            )}
            {budget.by_category.map((c) => (
              <div key={c.name} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ fontSize: 14 }}>{c.name}</Text>
                  <Text style={{ fontSize: 14, fontWeight: 600 }}>{fmt(c.amount || 0, cur)}</Text>
                </div>
                <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginTop: -2 }}>
                  {c.count} {c.count === 1 ? 'расход' : c.count < 5 ? 'расхода' : 'расходов'}
                </Text>
              </div>
            ))}
          </Card>

          <SectionTitle>Ближайшие платежи</SectionTitle>
          <Card style={{ padding: 16 }}>
            {budget.unpaid_payments.length === 0 && (
              <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>
                Все платежи оплачены
              </Text>
            )}
            {budget.unpaid_payments.map((p, i) => (
              <div key={i} style={{
                padding: '12px 0',
                borderBottom: i < budget.unpaid_payments.length - 1
                  ? '1px solid var(--vkui--color_separator_primary)' : 'none',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <Text style={{ fontSize: 15, fontWeight: 500 }}>{p.name}</Text>
                    <Text style={{ fontSize: 13, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                      {p.expense}
                    </Text>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Text style={{ fontSize: 15, fontWeight: 600 }}>{fmt(p.amount, cur)}</Text>
                    <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)', marginTop: 2 }}>
                      {p.due_date ? `до ${new Date(p.due_date).toLocaleDateString('ru-RU')}` : 'без срока'}
                    </Text>
                  </div>
                </div>
              </div>
            ))}
          </Card>
        </Div>
      )}

      {/* ===== TASKS ===== */}
      {!loading && activeTab === 'tasks' && tasks && (
        <Div style={{ paddingTop: 16 }}>
          <SectionTitle>Выполнение чек-листа</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <Text style={{ fontSize: 14, color: 'var(--vkui--color_text_secondary)' }}>
                Готово {tasks.done} из {tasks.total}
              </Text>
              <Headline level="1" style={{ fontSize: 28 }}>{tasks.progress_pct}%</Headline>
            </div>
            <Progress value={tasks.progress_pct} style={{ height: 10 }} />
          </Card>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <StatCard title="Всего задач" value={tasks.total} />
            <StatCard title="Выполнено" value={tasks.done} accent />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <StatCard title="Просрочено" value={tasks.overdue} />
            <StatCard title="Осталось" value={tasks.open} />
          </div>

          <SectionTitle>Сроки</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            <BarRow label="Выполнено" value={tasks.done} max={Math.max(tasks.total, 1)} color="#3bc26b" />
            <BarRow label="Предстоит сделать" value={tasks.upcoming} max={Math.max(tasks.total, 1)} color="#ff9f0a" />
            <BarRow label="Просрочено" value={tasks.overdue} max={Math.max(tasks.total, 1)} color="#e64646" />
          </Card>

          <SectionTitle>Задачи по категориям</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            {tasks.by_category.length === 0 && (
              <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Категорий пока нет</Text>
            )}
            {tasks.by_category.map((c) => (
              <BarRow
                key={c.name}
                label={`${c.name} — выполнено ${c.done} из ${c.total}`}
                value={c.done}
                max={Math.max(c.total, 1)}
              />
            ))}
          </Card>

          <SectionTitle>Подзадачи</SectionTitle>
          <Card style={{ padding: 16 }}>
            <BarRow
              label="Подзадачи выполнены"
              value={tasks.subtasks_done}
              max={Math.max(tasks.subtasks_total, 1)}
              suffix={`из ${tasks.subtasks_total}`}
            />
          </Card>
        </Div>
      )}

      {/* ===== GUESTS ===== */}
      {!loading && activeTab === 'guests' && guests && (
        <Div style={{ paddingTop: 16 }}>
          <SectionTitle>Гости</SectionTitle>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <StatCard title="Всего гостей" value={guests.total} accent />
            <StatCard title="Пары" value={guests.couples} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <StatCard title="Ещё не приглашены" value={guests.not_invited} />
          </div>

          <SectionTitle>Ответы на приглашения (RSVP)</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            <BarRow label="Подтвердили" value={guests.rsvp.accepted} max={guests.total} color="#3bc26b" />
            <BarRow label="Ждём ответа" value={guests.rsvp.pending} max={guests.total} color="#ff9f0a" />
            <BarRow label="Отказались" value={guests.rsvp.denied} max={guests.total} color="#e64646" />
            <BarRow label="Не приглашены" value={guests.not_invited} max={guests.total} color="#8e8e93" />
          </Card>

          <SectionTitle>Состав по возрасту</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            <BarRow label="Взрослые" value={guests.by_age.adult} max={guests.total} />
            <BarRow label="Дети" value={guests.by_age.child} max={guests.total} />
            <BarRow label="Младенцы" value={guests.by_age.baby} max={guests.total} />
          </Card>

          <SectionTitle>Гости по группам</SectionTitle>
          <Card style={{ padding: 16 }}>
            {guests.by_group.length === 0 && (
              <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Групп пока нет</Text>
            )}
            {guests.by_group.map((g) => (
              <BarRow key={g.name} label={g.name} value={g.count} max={guests.total} />
            ))}
          </Card>
        </Div>
      )}

      {/* ===== VENDORS ===== */}
      {!loading && activeTab === 'vendors' && vendors && (
        <Div style={{ paddingTop: 16 }}>
          <SectionTitle>Подрядчики</SectionTitle>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            <StatCard title="Всего подрядчиков" value={vendors.total} accent />
            <StatCard title="Общая сумма" value={fmt(vendors.amount_total, cur)} />
          </div>

          <SectionTitle>Статусы</SectionTitle>
          <Card style={{ padding: 16, marginBottom: 20 }}>
            <BarRow label="Ожидает ответа" value={vendors.by_status.pending} max={vendors.total} color="#ff9f0a" />
            <BarRow label="Забронирован" value={vendors.by_status.reserved} max={vendors.total} color="#3bc26b" />
            <BarRow label="Отклонён" value={vendors.by_status.rejected} max={vendors.total} color="#e64646" />
          </Card>

          <SectionTitle>Подрядчики по категориям</SectionTitle>
          <Card style={{ padding: 16 }}>
            {vendors.by_category.length === 0 && (
              <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>Категорий пока нет</Text>
            )}
            {vendors.by_category.map((c) => (
              <div key={c.name} style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 14 }}>{c.name}</Text>
                  <Text style={{ fontSize: 14, fontWeight: 600 }}>{fmt(c.amount || 0, cur)}</Text>
                </div>
                <Text style={{ fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
                  {c.count} {c.count === 1 ? 'подрядчик' : c.count < 5 ? 'подрядчика' : 'подрядчиков'}
                </Text>
              </div>
            ))}
          </Card>
        </Div>
      )}

      <Div style={{ paddingTop: 20, paddingBottom: 24 }}>
        <Button size="l" mode="secondary" stretched onClick={() => void load()}>
          Обновить данные
        </Button>
      </Div>
    </Panel>
  );
}
